/**
 * SellerOnboardingPage.js
 * ========================
 * Page publique d'activation du compte vendeur via token magique.
 * Pas d'authentification requise pour accéder. Token est validé côté backend.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import {
  CheckCircle2, Lock, Loader2, ShieldCheck, Eye, Star, Building2,
} from 'lucide-react';

const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '') + '/api';

export default function SellerOnboardingPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('Lien invalide.');
      setLoading(false);
      return;
    }
    (async () => {
      try {
        const res = await axios.get(`${API_BASE}/mandate-upload/seller-invite/${token}`);
        setInfo(res.data);
      } catch (e) {
        setError(e.response?.data?.detail || 'Invitation invalide ou expirée');
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  const submit = async () => {
    if (password.length < 8) {
      toast.error('Mot de passe trop court (min 8 caractères)');
      return;
    }
    if (password !== confirm) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    if (!accepted) {
      toast.error("Vous devez accepter les conditions d'utilisation");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_BASE}/mandate-upload/seller-invite/accept`, {
        token,
        password,
      });
      // Login auto
      localStorage.setItem('auth_token', res.data.access_token);
      localStorage.setItem('pro_token', res.data.access_token);
      toast.success('Compte activé, bienvenue !');
      setTimeout(() => { window.location.href = '/seller/dashboard'; }, 600);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'activation");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950">
        <Skeleton className="h-96 w-full max-w-md" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950">
        <div className="rounded-2xl border bg-card p-8 max-w-md text-center" data-testid="invite-error">
          <div className="text-red-400 font-semibold mb-2">Invitation indisponible</div>
          <p className="text-sm text-muted-foreground">{error}</p>
          <Button variant="outline" className="mt-4" onClick={() => navigate('/login')}>Se connecter</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-slate-950 via-slate-900 to-orange-950">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-lg rounded-2xl bg-card border p-6 md:p-8" data-testid="seller-onboarding-page">
        <div className="text-center mb-6">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-500 to-red-500 items-center justify-center mb-3">
            <span className="text-2xl font-bold text-white">H</span>
          </div>
          <h1 className="text-2xl font-bold">Bienvenue {info?.first_name || ''}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {info?.agency_name && (<>L'agence <strong>{info.agency_name}</strong> · </>)}
            Mandat n°{info?.mandate_number}
          </p>
        </div>

        {/* Argumentaire */}
        <div className="grid grid-cols-3 gap-2 mb-6 text-center">
          <div className="rounded-lg bg-white/5 p-3">
            <Eye className="w-5 h-5 mx-auto text-orange-400 mb-1" />
            <div className="text-xs">Suivi visites</div>
          </div>
          <div className="rounded-lg bg-white/5 p-3">
            <Building2 className="w-5 h-5 mx-auto text-orange-400 mb-1" />
            <div className="text-xs">Agences mobilisées</div>
          </div>
          <div className="rounded-lg bg-white/5 p-3">
            <Star className="w-5 h-5 mx-auto text-orange-400 mb-1" />
            <div className="text-xs">Noter mon agent</div>
          </div>
        </div>

        {info?.has_existing_account ? (
          <div className="rounded-xl bg-blue-500/10 border border-blue-500/20 p-4 text-sm mb-4">
            Vous avez déjà un compte. Saisissez votre mot de passe pour finaliser l'association.
          </div>
        ) : null}

        <div className="space-y-3" data-testid="onboarding-form">
          <div>
            <Label>Email</Label>
            <Input value={info?.email || ''} disabled />
          </div>
          <div>
            <Label>Mot de passe (min 8 caractères) *</Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="password-input" />
          </div>
          <div>
            <Label>Confirmer le mot de passe *</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-input" />
          </div>
          <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer mt-3" data-testid="cgu-checkbox">
            <input type="checkbox" checked={accepted} onChange={(e) => setAccepted(e.target.checked)} className="mt-0.5" />
            <span>
              J'accepte les <a href="/cgu" target="_blank" rel="noreferrer" className="text-orange-400 underline">CGU</a>{' '}
              et la <a href="/privacy" target="_blank" rel="noreferrer" className="text-orange-400 underline">Politique de confidentialité</a>.
            </span>
          </label>

          <Button onClick={submit} disabled={submitting} className="w-full bg-orange-500 hover:bg-orange-600 mt-3" data-testid="activate-btn">
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <ShieldCheck className="w-4 h-4 mr-2" />}
            Activer mon espace vendeur
          </Button>
        </div>
      </motion.div>
    </div>
  );
}
